import { z } from 'zod';
import {
  prefixed,
  zAchievementId,
  zAvatarFrameId,
  zAvatarPartId,
  zBadgeId,
  zBoxDefId,
  zChapterId,
  zI18nKey,
  zItemId,
  zItemKind,
  zMiniGameId,
  zNpcId,
  zPhotoFrameId,
  zPostcardId,
  zProductCategory,
  zRoomId,
  zSceneId,
  zStickerId,
  zTaskId,
  zTitleId,
  zUnlockId,
  zZoneId,
} from './ids';
import { zCondition, zGrant, zRewards } from './progression';
import { zGridRect } from './rooms';

// ---- Unlocks (content-schemas §4.1) ------------------------------------------------------------

export const zUnlockDef = z
  .object({
    id: zUnlockId,
    nameKey: zI18nKey,
    reasonKey: zI18nKey,
    prerequisites: z.array(zCondition).max(6),
    grants: z.array(zGrant),
    previewWhenLocked: z.boolean().default(true),
    order: z.number().int(),
  })
  .refine((u) => u.prerequisites.filter((c) => c.type === 'coinsCost').length <= 1, 'at most one coinsCost');
export type UnlockDefInput = z.input<typeof zUnlockDef>;
export type UnlockDef = z.output<typeof zUnlockDef>;

// ---- Stats & achievements (§4.2–4.3) -----------------------------------------------------------

export const zCounterStat = z.enum([
  'itemsPlaced',
  'rightSpotPlacements',
  'itemsRotated',
  'productsDisplayed',
  'unitsSold',
  'salesCount',
  'ordersFulfilled',
  'ordersSkipped',
  'stockBoxesBought',
  'itemsPurchased',
  'boxesOpened',
  'catPets',
  'npcTalks',
  'photosTaken',
  'miniGamesCompleted',
  'mailClaimed',
  'chaptersSeen',
  'shootsCompleted',
  'shootsExcellent',
]);
export type CounterStat = z.output<typeof zCounterStat>;

export const zDistinctStat = z.enum([
  'rightSpotItemIds',
  'ownedDecorFurnitureIds',
  'npcsTalkedTo',
  'setsCompleted',
  'itemsEverOwned',
]);
export type DistinctStat = z.output<typeof zDistinctStat>;

export const zAchievementDef = z
  .object({
    id: zAchievementId,
    category: z.enum(['organizing', 'shopkeeping', 'decorating', 'social', 'moments']),
    tier: z.enum(['bronze', 'silver', 'gold']),
    nameKey: zI18nKey,
    descKey: zI18nKey,
    hintKey: zI18nKey.optional(),
    secret: z.boolean().default(false),
    progress: z.discriminatedUnion('kind', [
      z.object({ kind: z.literal('counter'), stat: zCounterStat, target: z.number().int().positive() }),
      z.object({ kind: z.literal('distinct'), stat: zDistinctStat, target: z.number().int().positive() }),
      z.object({ kind: z.literal('condition'), condition: zCondition }),
    ]),
    rewards: zRewards,
    order: z.number().int(),
  })
  .refine((a) => !a.secret || a.hintKey !== undefined, 'secret achievements need a hintKey');
export type AchievementDefInput = z.input<typeof zAchievementDef>;
export type AchievementDef = z.output<typeof zAchievementDef>;

// ---- Gentle Suggestions (§4.4) -----------------------------------------------------------------

export const zEventMatcher = z.object({
  itemId: zItemId.optional(),
  itemIdsAny: z.array(zItemId).optional(),
  kindsAny: z.array(zItemKind).optional(),
  tagsAny: z.array(z.string()).optional(),
  roomId: zRoomId.optional(),
  zone: zZoneId.optional(),
  rightSpot: z.boolean().optional(),
  fromBag: z.boolean().optional(),
  acquiredBy: z
    .enum(['starter', 'purchase', 'gift', 'story', 'reward', 'mail', 'preplaced', 'dialogue'])
    .optional(),
  rotated: z.boolean().optional(),
  boxDefIdsAny: z.array(zBoxDefId).optional(),
  miniGameId: zMiniGameId.optional(),
  level: z.number().int().optional(),
  passed: z.boolean().optional(),
  completed: z.boolean().optional(),
  chapterId: zChapterId.optional(),
  npcId: zNpcId.optional(),
});
export type EventMatcher = z.output<typeof zEventMatcher>;

export const zTaskCriteria = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('event'),
    events: z.array(z.string()).min(1),
    where: zEventMatcher.optional(),
    count: z.number().int().positive(),
    distinctBy: z.enum(['itemId', 'boxDefId', 'npcId']).optional(),
  }),
  z.object({
    kind: z.literal('stat'),
    stat: zCounterStat,
    target: z.number().int().positive(),
    relative: z.boolean(),
  }),
  z.object({ kind: z.literal('conditions'), all: z.array(zCondition).min(1) }),
  z.object({
    kind: z.literal('placed'),
    where: zEventMatcher.pick({
      itemId: true,
      itemIdsAny: true,
      kindsAny: true,
      tagsAny: true,
      roomId: true,
      zone: true,
      rightSpot: true,
      acquiredBy: true,
    }),
    count: z.number().int().positive(),
  }),
]);
export type TaskCriteria = z.output<typeof zTaskCriteria>;

export const zHighlightTarget = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('ui'), elementId: z.string() }),
  z.object({ kind: z.literal('sceneObject'), sceneId: zSceneId, ref: z.string() }),
  z.object({ kind: z.literal('cell'), roomId: zRoomId, rect: zGridRect }),
]);

export const zSuggestionDef = z.object({
  id: zTaskId,
  kind: z.enum(['tutorial', 'optional', 'repeatable']),
  order: z.number().int(),
  titleKey: zI18nKey,
  hintKey: zI18nKey.optional(),
  /** Item whose `buyPrice` fills `{price}` in the hint (T09: "đèn bàn: {price} xu"). */
  hintPriceItemId: zItemId.optional(),
  requires: z.array(zCondition).default([]),
  criteria: zTaskCriteria,
  rewards: zRewards,
  autoClaim: z.boolean(),
  highlight: zHighlightTarget.optional(),
  /** "Đi tới" (ux-screens §3.8): where the task can be done. Room first, then a panel or a mini game intro. */
  goto: z
    .object({
      room: zRoomId.optional(),
      panel: z.enum(['catalog', 'stock', 'orders', 'town', 'photo', 'tasks']).optional(),
      miniGameId: zMiniGameId.optional(),
    })
    .optional(),
});
export type SuggestionDefInput = z.input<typeof zSuggestionDef>;
export type SuggestionDef = z.output<typeof zSuggestionDef>;

// ---- NPCs, dialogue, orders (§5) ---------------------------------------------------------------

export const zDialogueId = prefixed('dlg_');
export const zOrderTemplateId = prefixed('ord_');
export const zLetterIdRef = prefixed('ltr_');
export const zFlagIdRef = prefixed('flag_');

export const zNpcDef = z.object({
  id: zNpcId,
  kind: z.enum(['human', 'pet']),
  nameKey: zI18nKey,
  portrait: z.string(),
  sprite: z.string(),
  favorites: z.object({ categories: z.array(zProductCategory), itemIds: z.array(zItemId) }),
  thresholdsRef: z.enum(['npc', 'pet']),
  levels: z.array(
    z.object({ level: z.number().int().min(1), rewards: zRewards, thanksDialogueId: zDialogueId.optional() }),
  ),
  talk: z.array(
    z.object({
      dialogueId: zDialogueId,
      conditions: z.array(zCondition).default([]),
      priority: z.number().int(),
      once: z.boolean().default(false),
    }),
  ),
  orderTemplates: z.array(zOrderTemplateId),
  firstOrderTemplateId: zOrderTemplateId.optional(),
  metFlag: zFlagIdRef,
  canShop: z.boolean(),
  letterIds: z.array(zLetterIdRef),
});
export type NpcDefInput = z.input<typeof zNpcDef>;
export type NpcDef = z.output<typeof zNpcDef>;

export const zDialogueEffect = z.discriminatedUnion('type', [
  z.object({ type: z.literal('grantItem'), itemId: zItemId, qty: z.number().int().positive().default(1) }),
  z.object({ type: z.literal('friendship'), npcId: zNpcId, amount: z.number().int().min(1).max(10) }),
  z.object({ type: z.literal('setFlag'), flag: zFlagIdRef, value: z.boolean() }),
  z.object({ type: z.literal('unlockChapter'), chapterId: zChapterId }),
]);
export type DialogueEffect = z.output<typeof zDialogueEffect>;

export const zDialogueSpeaker = z.enum([
  'grandma',
  'grandma_letter',
  'grandma_note',
  'narrator',
  'player',
  'npc_lan',
  'npc_bao',
  'npc_mit',
  'npc_boss_cat',
  'customer',
]);

/**
 * Portrait expression for a line (style-guide §11.3). The overlay shows `<portrait base>_<expression>` and falls
 * back to the neutral portrait when that image does not exist.
 */
export const zDialogueExpression = z.enum([
  'neutral',
  'happy',
  'surprised',
  'sweat',
  'sleepy',
  'wink',
  'thinking',
  'meow',
]);
export type DialogueExpression = z.output<typeof zDialogueExpression>;

export const zDialogueDef = z
  .object({
    id: zDialogueId,
    nodes: z
      .array(
        z.object({
          id: z.string().min(1),
          speaker: zDialogueSpeaker,
          textKey: zI18nKey,
          expression: zDialogueExpression.optional(),
          next: z.string().nullable().optional(),
          choices: z
            .array(
              z.object({
                textKey: zI18nKey,
                next: z.string().nullable(),
                effects: z.array(zDialogueEffect).optional(),
              }),
            )
            .min(1)
            .max(2)
            .optional(),
          effects: z.array(zDialogueEffect).optional(),
          condition: zCondition.optional(),
        }),
      )
      .min(1),
  })
  .superRefine((d, ctx) => {
    const ids = new Set(d.nodes.map((n) => n.id));
    if (ids.size !== d.nodes.length) ctx.addIssue({ code: 'custom', message: `${d.id}: duplicate node id` });
    for (const n of d.nodes) {
      for (const t of [n.next, ...(n.choices ?? []).map((c) => c.next)]) {
        if (t != null && !ids.has(t))
          ctx.addIssue({ code: 'custom', message: `${d.id}/${n.id} → ${t} missing` });
      }
      for (const c of n.choices ?? []) {
        for (const e of c.effects ?? []) {
          if (e.type === 'friendship' && e.amount > 1) {
            ctx.addIssue({ code: 'custom', message: `${d.id}/${n.id}: choice friendship must be ≤ 1` });
          }
        }
      }
    }
  });
export type DialogueDefInput = z.input<typeof zDialogueDef>;
export type DialogueDef = z.output<typeof zDialogueDef>;

export const zOrderTemplateDef = z.object({
  id: zOrderTemplateId,
  npcId: zNpcId,
  isFirst: z.boolean(),
  picks: z
    .array(
      z.object({
        from: z.object({
          itemIds: z.array(zItemId).optional(),
          categories: z.array(zProductCategory).optional(),
          favoritesOnly: z.boolean().optional(),
        }),
        qty: z.object({ min: z.number().int().positive(), max: z.number().int().positive() }),
      }),
    )
    .min(1)
    .max(2),
  totalQty: z.object({ min: z.number().int().positive(), max: z.number().int().positive() }),
  rewardMultiplier: z.number().positive().optional(),
  friendship: z.number().int().positive().optional(),
  textKey: zI18nKey,
  thanksKey: zI18nKey,
  conditions: z.array(zCondition).default([]),
  weight: z.number().positive(),
});
export type OrderTemplateDefInput = z.input<typeof zOrderTemplateDef>;
export type OrderTemplateDef = z.output<typeof zOrderTemplateDef>;

// ---- Mailbox, postcards, letters, chapters (§6) ------------------------------------------------

export const zMailEntry = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('postcard') }),
  z.object({ kind: z.literal('coins'), amount: z.number().int().positive() }),
  z.object({
    kind: z.literal('miniBox'),
    productKinds: z.number().int().positive(),
    unitsEach: z.number().int().positive(),
  }),
  z.object({ kind: z.literal('npcLetter'), rotation: z.array(zNpcId).min(1) }),
  z.object({ kind: z.literal('sticker') }),
]);

export const zMailboxCycle = z.object({
  cycleLength: z.literal(7),
  days: z
    .array(z.object({ day: z.number().int().min(1).max(7), entries: z.array(zMailEntry).min(1) }))
    .length(7),
  postcardOrder: z.array(prefixed('pc_')).min(1),
  stickerPool: z.array(zStickerId),
  stickerExhaustedCoins: z.number().int().positive(),
  preChapter2Letter: z.object({
    letterId: zLetterIdRef,
    requiresFlag: zFlagIdRef,
    fallbackCoins: z.number().int().positive(),
  }),
  fallbackProducts: z.array(prefixed('prod_')).min(1),
});
export type MailboxCycleInput = z.input<typeof zMailboxCycle>;
export type MailboxCycle = z.output<typeof zMailboxCycle>;

export const zPostcardDef = z.object({
  id: prefixed('pc_'),
  order: z.number().int(),
  titleKey: zI18nKey,
  bodyKey: zI18nKey,
  image: z.string(),
  repeatImage: z.string().optional(),
  special: z.boolean(),
  attachments: zRewards.default([]),
});
export type PostcardDefInput = z.input<typeof zPostcardDef>;
export type PostcardDef = z.output<typeof zPostcardDef>;

export const zLetterDef = z.object({
  id: zLetterIdRef,
  from: zNpcId,
  order: z.number().int().min(1),
  bodyKey: zI18nKey,
  requiresMet: z.boolean(),
  attachments: zRewards.default([]),
});
export type LetterDefInput = z.input<typeof zLetterDef>;
export type LetterDef = z.output<typeof zLetterDef>;

export const zStoryChapterDef = z.object({
  id: zChapterId,
  order: z.number().int(),
  titleKey: zI18nKey,
  albumKey: zI18nKey,
  dialogueId: zDialogueId,
  unlockedBy: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('newGame') }),
    z.object({ kind: z.literal('box'), boxDefId: zBoxDefId }),
    z.object({ kind: z.literal('unlock'), unlockId: zUnlockId }),
  ]),
  onSeen: z.array(zGrant).default([]),
  skippable: z.literal(true),
  autoPrompt: z.boolean(),
  /** A chapter told by a special postcard plays as soon as that card is opened (ch5, story-dialogue §3.5). */
  playsWithPostcard: zPostcardId.optional(),
});
export type StoryChapterDefInput = z.input<typeof zStoryChapterDef>;
export type StoryChapterDef = z.output<typeof zStoryChapterDef>;

// ---- Cosmetics (§3.8) --------------------------------------------------------------------------

export const zCosmetics = z.object({
  titles: z.array(z.object({ id: zTitleId, nameKey: zI18nKey })),
  badges: z.array(z.object({ id: zBadgeId, nameKey: zI18nKey, icon: z.string() })),
  stickers: z.array(
    z.object({
      id: zStickerId,
      nameKey: zI18nKey,
      frame: z.string(),
      isDefault: z.boolean(),
      group: z.enum(['default', 'mit', 'photo', 'boss_cat', 'mail']),
    }),
  ),
  photoFrames: z.array(
    z.object({ id: zPhotoFrameId, nameKey: zI18nKey, overlay: z.string(), isDefault: z.boolean() }),
  ),
  avatarParts: z.array(
    z.object({
      id: zAvatarPartId,
      slot: z.enum(['hair', 'shirtColor', 'hat']),
      nameKey: zI18nKey,
      frame: z.string().optional(),
      tint: z.string().optional(),
      isDefault: z.boolean(),
    }),
  ),
  avatarFrames: z.array(z.object({ id: zAvatarFrameId, nameKey: zI18nKey, frame: z.string() })),
});
export type CosmeticsInput = z.input<typeof zCosmetics>;
export type Cosmetics = z.output<typeof zCosmetics>;

export { zAchievementId };
